import React from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  .docs-title {
    background: linear-gradient(110deg, #fbbf24 35%, #a78bfa 50%, #fbbf24 65%);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: docs-shimmer 8s linear infinite;
  }

  @keyframes docs-shimmer {
    0%   { background-position: 0% 50%; }
    100% { background-position: 200% 50%; }
  }

  .docs-dot-grid {
    background-image: radial-gradient(circle, rgba(251,191,36,0.12) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  @keyframes docs-fadeUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .docs-fade { animation: docs-fadeUp 0.7s ease forwards; }
  .docs-fade-1 { animation-delay: 0.05s; opacity: 0; }
  .docs-fade-2 { animation-delay: 0.15s; opacity: 0; }

  @keyframes docs-float {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-15px); }
  }
  .docs-float {
    animation: docs-float 4s ease-in-out infinite;
  }

  .docs-btn {
    position: relative; overflow: hidden;
    transition: all 0.3s ease;
  }
  .docs-btn::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent);
    transform: translateX(-100%);
    transition: transform 0.5s ease;
  }
  .docs-btn:hover::after { transform: translateX(100%); }
  .docs-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(217,119,6,0.35);
    background-color: #d97706;
  }
`;

const Icon = ({ d, size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
    strokeLinejoin="round" className={className}>
    <path d={d} />
  </svg>
);

const ICONS = {
  whatsapp: "M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z",
  book: "M4 19.5A2.5 2.5 0 016.5 17H20M4 19.5A2.5 2.5 0 016.5 22H20V2H6.5A2.5 2.5 0 004 4.5v15z",
};

const DocumentationPage = ({ setCurrentPage = () => {} }) => {
  return (
    <>
      <style>{styles}</style>
      <div className="space-y-20 max-w-6xl mx-auto px-4 pb-20">

          {/* ── HERO ─────────────────────────────────────────── */}
          <section className="relative pt-16 pb-10 text-center docs-fade docs-fade-1">
            <div className="docs-dot-grid absolute inset-0 rounded-2xl -z-10 opacity-60" />
            
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-600/30 
              bg-amber-900/10 mb-6">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-amber-400 text-xs font-semibold tracking-widest uppercase">
                COMING SOON
              </span>
            </div>

            <div className="mb-8 docs-float">
              <Icon d={ICONS.book} size={80} className="text-amber-400/30 mx-auto" />
            </div>

            <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-5">
              <span className="docs-title">Documentation</span>
            </h1>
            
            <p className="text-slate-400 max-w-xl mx-auto text-base leading-relaxed">
              Comprehensive guides, tutorials, and references to help you master HEKAOS 
              intelligence systems and integrate them into your workflows.
            </p>
          </section>

          {/* ── FEATURES PREVIEW ────────────────────────────── */}
          <section className="mb-32 docs-fade docs-fade-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { title: "Getting Started", desc: "Quick setup guides and installation instructions" },
                { title: "Integration Guides", desc: "Step-by-step tutorials for common use cases" },
                { title: "Best Practices", desc: "Tips and patterns from our engineering team" }
              ].map((item, i) => (
                <div key={i} 
                  className="rounded-2xl border border-amber-800/30 bg-gradient-to-br from-amber-900/10 to-slate-900/50 
                    p-6 backdrop-blur-sm hover:border-amber-700/50 transition-all duration-300">
                  <h3 className="text-amber-400 font-bold text-lg mb-2 tracking-tight">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* ── CTA SECTION ─────────────────────────────────── */}
          <section className="docs-fade docs-fade-2 text-center p-12 rounded-2xl bg-gradient-to-br from-amber-900/20 to-violet-900/20 border border-amber-800/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-amber-400 mb-4">
              Need Documentation Now?
            </h2>
            <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
              Our team can walk you through setup, integration, and best practices. 
              We're here to help you get started quickly.
            </p>
            
            <a href="https://wa.me/919235327048?text=Hi%2C%20I%20need%20help%20with%20HEKAOS%20documentation" 
              target="_blank" rel="noopener noreferrer"
              className="docs-btn inline-flex items-center gap-2 bg-amber-600 text-black px-8 py-3 rounded-lg font-bold no-underline text-sm">
              <Icon d={ICONS.whatsapp} size={16} />
              Chat With Our Team
            </a>
          </section>
      </div>
    </>
  );
};

export default DocumentationPage;